import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { cx } from "@/lib/cx";
import { DisclosureChevron, ROW_RECIPE } from "../list";
import { hoverReveal, TONE_TEXT, type Tone } from "../tones";
import { SURFACE_ICON, type CompactSurface } from "./marks";

export type RowRenderProps = { className: string; children: ReactNode };

export type RowProps = {
  /** A destination uses a real link; omit it for a reporting row. */
  to?: string;
  /**
   * Supplies a custom `<Link>`, `<button>` or plain `<div>` while retaining the
   * row's geometry and states. Takes precedence over `to`; navigating rows must
   * still render a link.
   */
  render?: (props: RowRenderProps) => ReactNode;
  /** The row wrapper is a list item only when its parent is a semantic list. */
  as?: "li" | "div";
  /** The surface this row belongs to; draws its tinted 14px glyph. */
  surface?: CompactSurface;
  /** Used when there is no `surface` — a flame, a lane mark. */
  icon?: ReactNode;
  /** A checkbox, tick or other control before the surface glyph. */
  leadingSlot?: ReactNode;
  /** Colours the glyph and, when `note` is a call to action, the note. */
  tone?: Tone;
  title: ReactNode;
  /** The quiet second line. Keep it to one clause; this is not prose. */
  note?: ReactNode;
  /** The right-aligned figure — semibold, tabular. */
  value?: string;
  /** The unit after it, quiet. */
  unit?: string;
  /** Hover-revealed row controls; touch and keyboard users always see them. */
  actions?: ReactNode;
  /** A control or compact readout aligned after the title block. */
  trailingSlot?: ReactNode;
  /** Gives a width-owning control a second line inside the same divided row. */
  stacked?: boolean;
  /** Associates the visible row title with a single trailing form control. */
  titleFor?: string;
  /** Quiets the title and the value — a streak with nothing to report. */
  dim?: boolean;
  /** Renders `note` in the tone rather than in muted: "do this to keep it". */
  urgent?: boolean;
  /**
   * Semibold title. Reserved for the single promoted call — a *weight*
   * channel, so the row can outrank its neighbours without spending the tint
   * that rule 4 keeps for perishability.
   */
  strong?: boolean;
  /** Code-list treatment with an optional line-number gutter. */
  mono?: boolean;
  lineNumber?: number;
  className?: string;
};

/**
 * A 40px destination or reporting row, rising to 44px for a coarse pointer.
 *
 * Two lines when there is a `note`, and it grows rather than clipping — but the
 * note is one clause by contract, so in practice it grows by exactly one line.
 * The hover fill runs the full width of the panel because the divider does too:
 * both are full-bleed, and a row whose fill stopped short of a divider that did
 * not would read as a misalignment. `render` is the escape hatch for a caller
 * that needs a different semantic element; `as` controls only the outer row.
 */
export function Row({
  to,
  render,
  as: Element = "div",
  surface,
  icon,
  leadingSlot,
  tone = "neutral",
  title,
  note,
  value,
  unit,
  actions,
  trailingSlot,
  stacked = false,
  titleFor,
  dim = false,
  urgent = false,
  strong = false,
  mono = false,
  lineNumber,
  className,
}: RowProps) {
  const Icon = surface ? SURFACE_ICON[surface] : null;
  const interactive = cx(
    "flex min-w-0 flex-1 gap-3 pl-4 text-left",
    ROW_RECIPE,
    mono && "font-mono",
    stacked ? "flex-col items-stretch py-3" : "items-center py-2",
    actions ? "pr-2" : "pr-4",
    (to || render) && "focus-inset",
  );

  const body = (
    <>
      {mono && lineNumber !== undefined ? (
        <span aria-hidden className="w-4 shrink-0 text-right text-caption tabular-nums text-subtle">{lineNumber}</span>
      ) : null}
      {leadingSlot ? (
        <span className="flex shrink-0 items-center justify-center">{leadingSlot}</span>
      ) : null}

      {Icon ? (
        <Icon
          className={cx("size-4 shrink-0", dim ? "text-faint" : TONE_TEXT[tone])}
        />
      ) : icon ? (
        <span
          className={cx(
            "flex size-4 shrink-0 items-center justify-center",
            dim ? "text-faint" : TONE_TEXT[tone],
          )}
        >
          {icon}
        </span>
      ) : null}

      <span className={cx("flex min-w-0 flex-1 flex-col", stacked && "w-full")}>
        {titleFor ? (
          <label
            htmlFor={titleFor}
            className={cx(
              mono ? "title-clamp text-caption" : "title-clamp text-body",
              strong && "font-semibold",
              dim ? "text-muted" : "text-ink",
            )}
          >
            {title}
          </label>
        ) : (
          <span
          className={cx(
            mono ? "title-clamp text-caption" : "title-clamp text-body",
            strong && "font-semibold",
            dim ? "text-muted" : "text-ink",
          )}
          >
            {title}
          </span>
        )}
        {note ? (
          <span
            className={cx(
              "truncate text-label",
              urgent ? TONE_TEXT[tone] : "text-subtle",
            )}
          >
            {note}
          </span>
        ) : null}
      </span>

      {trailingSlot ? (
        <span className={cx("flex min-w-0 items-center", stacked ? "w-full" : "shrink-0")}>
          {trailingSlot}
        </span>
      ) : null}

      {value ? (
        <span className="flex shrink-0 items-baseline gap-1">
          <span
            className={cx(
              "numeric text-label",
              dim ? "font-normal text-subtle" : "font-semibold text-ink",
            )}
          >
            {value}
          </span>
          {unit ? <span className="text-caption text-subtle">{unit}</span> : null}
        </span>
      ) : null}

      {to || render ? <DisclosureChevron className="shrink-0" /> : null}
    </>
  );

  return (
    <Element
      className={cx(
        "group/row flex min-w-0 [&:not(:first-child)]:hairline-t",
        (to || render) && "transition-colors dur-fast hover:bg-hover active:bg-active",
        className,
      )}
    >
      {render ? (
        render({ className: interactive, children: body })
      ) : to ? (
        <Link to={to} className={interactive}>
          {body}
        </Link>
      ) : (
        <div className={interactive}>{body}</div>
      )}
      {actions ? (
        <span
          className={cx(
            "flex shrink-0 items-center gap-1 self-center pr-3",
            hoverReveal,
          )}
        >
          {actions}
        </span>
      ) : null}
    </Element>
  );
}
